import type { GraphNode, NodePosition, PositionsMap, EdgeElements, NodeElements } from './types';
import { badgeSizes, badgeFilters, badgeOutlineColors, zoneOrder } from './config';

// Node sizing helpers
export function getNodeTier(node: GraphNode): 'minor' | 'major' | 'capstone' | 'root' {
	if (node.id === 'node1') return 'root';
	if (node.size === 'node_capstone') return 'capstone';
	if (node.size === 'node_major') return 'major';
	return 'minor';
}

export function getNodeSize(node: GraphNode): number {
	return badgeSizes[getNodeTier(node)];
}

export function getNodeRadius(node: GraphNode): number {
	return getNodeSize(node) / 2;
}

export function getConnectorInset(node: GraphNode): number {
	if (node.id === 'node1') return 4;
	if (node.size === 'node_capstone') return 8;
	if (node.size === 'node_major') return 5;
	return 4;
}

export function getNodeOrderHint(node: GraphNode): number {
	return node.coord?.x ?? node.sourceIndex ?? 0;
}

export function getNodeSpacing(leftNode: GraphNode, rightNode: GraphNode): number {
	const leftRadius = getNodeRadius(leftNode);
	const rightRadius = getNodeRadius(rightNode);
	let padding = 14;

	if (leftNode.size === 'node_capstone' || rightNode.size === 'node_capstone') padding += 12;
	if (leftNode.size === 'node_major' || rightNode.size === 'node_major') padding += 4;
	if (leftNode.zone !== rightNode.zone) padding += 8;

	return leftRadius + rightRadius + padding;
}

export function getBadgeStyle(node: GraphNode, badgeTint: string): string {
	const baseFilter = badgeFilters[badgeTint as keyof typeof badgeFilters] || badgeFilters.general;
	const outlineColor = badgeOutlineColors[badgeTint];

	if ((node.size === 'node_capstone' || node.size === 'node_major') && outlineColor) {
		const outline = [
			`drop-shadow(1px 0 0 ${outlineColor})`,
			`drop-shadow(-1px 0 0 ${outlineColor})`,
			`drop-shadow(0 1px 0 ${outlineColor})`,
			`drop-shadow(0 -1px 0 ${outlineColor})`
		].join(' ');
		return `filter: ${baseFilter} ${outline};`;
	}

	return `filter: ${baseFilter};`;
}

// Math helpers
export function median(values: number[]): number | null {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const middle = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

export function average(values: number[]): number | null {
	if (values.length === 0) return null;
	return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

// Path building
export function getSidePoint(position: NodePosition, side: 'top' | 'bottom' | 'left' | 'right'): { x: number; y: number } {
	const radius = getNodeRadius(position.node);
	const inset = getConnectorInset(position.node);

	if (side === 'top') return { x: position.x, y: position.y - radius + inset };
	if (side === 'bottom') return { x: position.x, y: position.y + radius - inset };
	if (side === 'left') return { x: position.x - radius + inset, y: position.y };
	return { x: position.x + radius - inset, y: position.y };
}

export function buildOrthogonalPath(
	start: NodePosition,
	end: NodePosition,
	transitionChannels: Record<string, number>,
	preferStraight: boolean
): string {
	const sourcePoint = getSidePoint(start, 'bottom');
	const targetPoint = getSidePoint(end, 'top');

	if (preferStraight && Math.abs(sourcePoint.x - targetPoint.x) < 0.75) {
		return `M ${sourcePoint.x} ${sourcePoint.y} L ${targetPoint.x} ${targetPoint.y}`;
	}

	const transitionKey = `${start.node.level}-${end.node.level}`;
	const midY = transitionChannels[transitionKey] ?? (sourcePoint.y + targetPoint.y) / 2;

	return [
		`M ${sourcePoint.x} ${sourcePoint.y}`,
		`L ${sourcePoint.x} ${midY}`,
		`L ${targetPoint.x} ${midY}`,
		`L ${targetPoint.x} ${targetPoint.y}`
	].join(' ');
}

// Exclusive chain detection
export function isExclusiveVerticalChain(
	parentId: string,
	childId: string,
	nodes: Record<string, GraphNode>
): boolean {
	const parent = nodes[parentId];
	const child = nodes[childId];
	const previousLevelParents = child.parents.filter(
		(ancestorId) => nodes[ancestorId] && nodes[ancestorId].level === child.level - 1
	);
	const nextLevelChildren = parent.children.filter(
		(descendantId) => nodes[descendantId] && nodes[descendantId].level === parent.level + 1
	);
	return (
		previousLevelParents.length === 1 &&
		previousLevelParents[0] === parentId &&
		nextLevelChildren.length === 1 &&
		nextLevelChildren[0] === childId
	);
}

// Layout sorting helpers
export function sortLevelWithNeighbors(
	level: number,
	adjacentLevel: number,
	useParents: boolean,
	nodes: Record<string, GraphNode>,
	zoneLevels: Record<string, Record<number, string[]>>,
	zoneOrder: readonly string[]
): void {
	const adjacentOrder: string[] = [];
	zoneOrder.forEach((zone) => {
		(zoneLevels[zone][adjacentLevel] || []).forEach((id) => adjacentOrder.push(id));
	});
	const adjacentPosition = new Map(adjacentOrder.map((id, index) => [id, index]));

	zoneOrder.forEach((zone) => {
		const ids = zoneLevels[zone][level] || [];
		if (ids.length < 2) return;

		ids.sort((leftId, rightId) => {
			const leftRefs = (useParents ? nodes[leftId].parents : nodes[leftId].children)
				.filter((id) => nodes[id] && nodes[id].level === adjacentLevel && adjacentPosition.has(id))
				.map((id) => adjacentPosition.get(id)!);
			const rightRefs = (useParents ? nodes[rightId].parents : nodes[rightId].children)
				.filter((id) => nodes[id] && nodes[id].level === adjacentLevel && adjacentPosition.has(id))
				.map((id) => adjacentPosition.get(id)!);

			const leftScore = median(leftRefs);
			const rightScore = median(rightRefs);

			if (leftScore !== null && rightScore !== null && leftScore !== rightScore) {
				return leftScore - rightScore;
			}
			if (leftScore !== null && rightScore === null) return -1;
			if (leftScore === null && rightScore !== null) return 1;

			return (
				getNodeOrderHint(nodes[leftId]) - getNodeOrderHint(nodes[rightId]) ||
				nodes[leftId].sourceIndex - nodes[rightId].sourceIndex
			);
		});
	});
}

export function clusterLevelByParents(
	level: number,
	nodes: Record<string, GraphNode>,
	zoneLevels: Record<string, Record<number, string[]>>,
	zoneOrder: readonly string[]
): void {
	const previousOrder: string[] = [];
	zoneOrder.forEach((zone) => {
		(zoneLevels[zone][level - 1] || []).forEach((id) => previousOrder.push(id));
	});
	const previousPosition = new Map(previousOrder.map((id, index) => [id, index]));

	zoneOrder.forEach((zone) => {
		const ids = zoneLevels[zone][level] || [];
		if (ids.length < 2) return;

		const groups = new Map<
			string,
			{ ids: string[]; previousParents: string[] }
		>();

		ids.forEach((id) => {
			const previousParents = nodes[id].parents
				.filter((parentId) => nodes[parentId] && nodes[parentId].level === level - 1)
				.sort(
					(leftId, rightId) =>
						(previousPosition.get(leftId) ?? Number.MAX_SAFE_INTEGER) -
						(previousPosition.get(rightId) ?? Number.MAX_SAFE_INTEGER)
				);

			const key = previousParents.length > 0 ? previousParents.join('|') : `solo:${id}`;
			if (!groups.has(key)) {
				groups.set(key, { ids: [], previousParents });
			}
			groups.get(key)!.ids.push(id);
		});

		const orderedGroups = [...groups.values()].sort((leftGroup, rightGroup) => {
			const leftScore = median(
				leftGroup.previousParents
					.map((parentId) => previousPosition.get(parentId))
					.filter((v): v is number => v !== undefined)
			);
			const rightScore = median(
				rightGroup.previousParents
					.map((parentId) => previousPosition.get(parentId))
					.filter((v): v is number => v !== undefined)
			);

			if (leftScore !== null && rightScore !== null && leftScore !== rightScore) {
				return leftScore - rightScore;
			}
			if (leftScore !== null && rightScore === null) return -1;
			if (leftScore === null && rightScore !== null) return 1;

			return (
				getNodeOrderHint(nodes[leftGroup.ids[0]]) - getNodeOrderHint(nodes[rightGroup.ids[0]]) ||
				nodes[leftGroup.ids[0]].sourceIndex - nodes[rightGroup.ids[0]].sourceIndex
			);
		});

		orderedGroups.forEach((group) => {
			group.ids.sort(
				(leftId, rightId) =>
					getNodeOrderHint(nodes[leftId]) - getNodeOrderHint(nodes[rightId]) ||
					nodes[leftId].sourceIndex - nodes[rightId].sourceIndex
			);
		});

		zoneLevels[zone][level] = orderedGroups.flatMap((group) => group.ids);
	});
}
